import './CommentForm.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { incline } from 'lvovich';
import { useDispatch, useSelector } from 'react-redux';

import { IcClose16, IcGallery28, IcSend28 } from '../../../assets';
import { RootState } from '$store/rootReducer';
import { createPostFormSelector, postsActions } from '../../../store/posts';
import AttachPickerManager from '../AttachPickerManager/AttachPickerManager';
import { AttachPickerManagerRef } from '../AttachPickerManager/AttachPickerManager.interface';
import {
  useCurrentUserInfo,
  useDesktop,
  useEvent,
  useGroupInfo,
  usePost,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { ActionSheet } from '$uikit';
import { CommentFormProps } from '$shared/components/CommentForm/CommentForm.interface';
import { authSelector } from '$store/auth';
import { profileActions } from '$store/profile';
import { AuthPopoverState } from '$store/models';

const inputPadding = 8;
const inputLineHeight = 24;
const inputMinHeight = inputLineHeight + inputPadding * 2;
const inputMaxLines = 3;

export const CommentForm: FC<CommentFormProps> = ({ parentId }) => {
  const dispatch = useDispatch();
  const [height, setHeight] = useState(inputMinHeight);
  const form = useSelector((state: RootState) =>
    createPostFormSelector(state, `comment_${parentId}`),
  );
  const { isLogged } = useSelector(authSelector);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const sendBtn = useRef<HTMLDivElement | null>(null);
  const isDesktop = useDesktop();
  const [bottom, setBottom] = useState(0);
  const replyToUser = useUserInfo(form.replyTo?.publisherId ?? 0);
  const curUser = useCurrentUserInfo();
  const [isMenuShown, setMenuShown] = useState(false);
  const t = useTranslator();

  const post = usePost(parentId);
  const postGroup = useGroupInfo(-post.ownerId);

  const isCanReplyFromGroup = useMemo(() => {
    if (!post || post.level !== 0) {
      return false;
    }

    if (!postGroup || !postGroup.isModer) {
      return false;
    }

    return true;
  }, [post, postGroup]);

  const handleOnChange = useCallback(
    (event) => {
      event.target.style.height = '0px';

      const newHeight = Math.min(
        Math.max(inputMinHeight, event.target.scrollHeight),
        inputLineHeight * inputMaxLines + inputPadding * 2,
      );

      event.target.style.height = `${newHeight}px`;
      setHeight(newHeight);

      dispatch(
        postsActions.updateFormText({
          formId: `comment_${parentId}`,
          text: event.target.value,
        }),
      );
    },
    [dispatch, parentId, setHeight],
  );

  const handleSend = useCallback(
    (e) => {
      try {
        e && e.preventDefault();
      } catch (err) {}

      if (isLogged) {
        dispatch(postsActions.publishComment(parentId));
      } else {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      }
    },
    [dispatch, parentId, isLogged],
  );

  useEffect(() => {
    if (form.text === '') {
      setHeight(inputMinHeight);
    }
  }, [form.text]);

  // @ts-ignore
  if (window.isNative) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEvent('touchstart', handleSend, sendBtn);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEvent('touchstart', handleSend, sendBtn);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEvent('mousedown', handleSend, sendBtn);
  }

  const handlePhotoChange = useCallback(
    ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      if (!isLogged) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
        return;
      }
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey, isLogged],
  );

  const handleScroll = useCallback(
    ({ target }) => {
      if (target.classList.contains('Modal__cont')) {
        setBottom(target.scrollTop);
      }
    },
    [setBottom],
  );

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    const el = document.querySelector('.Modal__cont');
    if (!el) {
      return;
    }

    el.addEventListener('scroll', handleScroll, true);
    return () => el.removeEventListener('scroll', handleScroll, true);
  }, [isDesktop, parentId]);

  const replyToLabel = useMemo(() => {
    if (form.replyTo) {
      const dativeName = incline(
        {
          first: replyToUser?.firstName ?? '',
          last: replyToUser?.lastName ?? '',
        },
        'dative',
      );
      return `${t('comment_reply')} ${dativeName.first} ${dativeName?.last || ''}`;
    }

    return null;
  }, [form.replyTo, replyToUser, t]);

  const placeholder = useMemo(() => {
    if (replyToLabel) {
      return replyToLabel;
    }

    return t('comment_placeholder');
  }, [replyToLabel, t]);

  const handleResetReply = useCallback(() => {
    dispatch(postsActions.replyComment({ parentId, comment: null }));
  }, [dispatch, parentId]);

  const handleMenu = useCallback(
    (value) => {
      setMenuShown(false);
      if (!isLogged) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
        return;
      }

      dispatch(postsActions.setSender({ formId: `comment_${parentId}`, sender: value }));
    },
    [dispatch, setMenuShown, parentId, isLogged],
  );

  const handleShowMenu = useCallback(() => {
    setMenuShown(true);
  }, [setMenuShown]);

  return (
    <>
      {isMenuShown && (
        <ActionSheet
          isOpened
          options={[
            { label: t('comment_from_group'), value: 'group' },
            { label: t('comment_from_myself'), value: 'myself' },
          ]}
          onChange={handleMenu}
          selected={form.sender || 'myself'}
        />
      )}
      <div
        className="CommentForm"
        style={{ transform: `translateY(${bottom}px) translateZ(0)` }}
      >
        {replyToLabel && (
          <div className="CommentForm__reply_to" onClick={handleResetReply}>
            {replyToLabel}
            <div className="CommentForm__reply_to__cancel">
              <IcClose16 />
            </div>
          </div>
        )}
        <AttachPickerManager mini ref={attachManager} pickerId={`comment_${parentId}`} />
        <div className="CommentForm__content">
          <div className="CommentForm__button">
            <IcGallery28 />
            <input
              key={photoInputKey}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              multiple
            />
          </div>
          {isCanReplyFromGroup && (
            <div className="CommentForm__sender" onClick={handleShowMenu}>
              <div
                className="CommentForm__sender__photo"
                style={{
                  backgroundImage: `url(${
                    form.sender === 'group' ? postGroup?.photo : curUser.photo
                  })`,
                }}
              />
            </div>
          )}
          <div className="CommentForm__input_wrap">
            <textarea
              autoFocus={isDesktop}
              placeholder={placeholder}
              style={{ height }}
              onChange={handleOnChange}
              value={form.text}
              id="comment_form"
            />
          </div>
          <div className="CommentForm__button" ref={sendBtn}>
            <IcSend28 />
          </div>
        </div>
      </div>
      <div className="CommentForm__helper" />
    </>
  );
};
